import type { EventingContext } from '../../types/context';
import type {
  NormalizedInteraction,
  NormalizedView,
} from '../../types/normalized';
import type {
  EventingAdapter,
  NormalizedConversion,
  NormalizedPageView,
} from '../types';
import { MetaCapiClient } from './MetaCapiClient';

type Fbq = (...args: unknown[]) => void;

const STANDARD_EVENTS = new Set<NormalizedConversion['name']>([
  'Lead',
  'ViewContent',
]);

export interface MetaPixelAdapterOptions {
  readonly pixelId: string;
  readonly capiClient?: MetaCapiClient;
  readonly onError?: (error: unknown) => void;
}

export class MetaPixelAdapter implements EventingAdapter {
  readonly name = 'meta-pixel';

  private readonly capiClient: MetaCapiClient;
  private readonly onError?: (error: unknown) => void;

  constructor({
    pixelId,
    onError,
    capiClient = new MetaCapiClient({ pixelId, onError }),
  }: MetaPixelAdapterOptions) {
    this.capiClient = capiClient;
    this.onError = onError;
  }

  handleInteraction(
    _interaction: NormalizedInteraction,
    _context: EventingContext,
  ): void {
    return;
  }

  handleView(_view: NormalizedView, _context: EventingContext): void {
    return;
  }

  handlePageView(
    _pageView: NormalizedPageView,
    _context: EventingContext,
  ): void {
    try {
      (window as Window & { fbq?: Fbq }).fbq?.('track', 'PageView');
    } catch (error) {
      this.onError?.(error);
    }
  }

  handleConversion(
    conversion: NormalizedConversion,
    context: EventingContext,
  ): void {
    try {
      const method = STANDARD_EVENTS.has(conversion.name)
        ? 'track'
        : 'trackCustom';
      (window as Window & { fbq?: Fbq }).fbq?.(
        method,
        conversion.name,
        {},
        { eventID: conversion.eventId },
      );
    } catch (error) {
      this.onError?.(error);
    }

    try {
      this.capiClient.send(conversion, {
        tenantId: context.tenantId ?? '',
      });
    } catch (error) {
      this.onError?.(error);
    }
  }
}
